import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { CircleDollarSign, Download, Eye, Pencil, Plus, RefreshCw, Settings } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable, type RowAction } from "@/components/data-table/DataTable";
import { usePagedQuery } from "@/components/data-table/usePagedQuery";
import { ESTADOS_BR } from "@/lib/estados";
import { downloadBlob } from "@/lib/download";
import {
  atualizarCobrancas,
  downloadBackupAssinados,
  INSTITUICAO_PATH,
  type Instituicao,
  type InstituicaoFiltro,
} from "./api";

// Porte de app/controllers/consulta-instituicao.controller.js +
// app/templates/instituicao/consulta-instituicao.html. Sem modal — Editar,
// Configurações e Ficha levam pra rotas próprias (/Instituicao/Form,
// /Instituicao/Configuracao, /Instituicao/Ficha). O legado tem
// duas colunas de ações (Ações / Financeiro); consolidadas aqui numa só —
// o <DataTable> não modela múltiplas colunas de ação, e não há ganho real
// em generalizar isso pra uma única tela.
const QUERY_KEY = ["instituicao"];

const SITUACAO_ADMIN_LABEL: Record<string, string> = { A: "Ativo", T: "Trial", I: "Inativo", C: "Cancelado", E: "Teste" };
const SITUACAO_CS_LABEL: Record<string, string> = { ON: "Onboarding", OG: "Ongoing", EX: "Expansão", RE: "Retenção", RC: "Risco de Churn" };

const columns: ColumnDef<Instituicao, unknown>[] = [
  { accessorKey: "codigo", header: "Código", enableSorting: true },
  { accessorKey: "razaoSocial", header: "Razão Social", enableSorting: true },
  { accessorKey: "nomeFantasia", header: "Nome Fantasia", enableSorting: true },
  { accessorKey: "email", header: "Email", enableSorting: true },
  { accessorKey: "cnpj", header: "CNPJ", enableSorting: true },
  {
    accessorKey: "situacaoAdmin",
    header: "Situação",
    enableSorting: true,
    cell: ({ getValue }) => SITUACAO_ADMIN_LABEL[String(getValue() ?? "")] ?? String(getValue() ?? ""),
  },
  {
    accessorKey: "situacaoCs",
    header: "Situação CS",
    enableSorting: true,
    cell: ({ getValue }) => SITUACAO_CS_LABEL[String(getValue() ?? "")] ?? String(getValue() ?? ""),
  },
];

export default function ConsultaInstituicao() {
  const navigate = useNavigate();
  const query = usePagedQuery<Instituicao, InstituicaoFiltro>({
    key: QUERY_KEY,
    path: INSTITUICAO_PATH,
    initialFiltro: {},
  });

  // O legado mostra um indicador global (não por linha) de "Gerando
  // arquivo" — reproduzido com o próprio estado da mutation, sem precisar de
  // um `disabled` por linha que o <DataTable> não modela hoje.
  const backup = useMutation({
    mutationFn: downloadBackupAssinados,
    onSuccess: (result) => downloadBlob(result.blob, result.filename),
    onError: () => toast.error("Falha ao gerar o backup dos arquivos assinados."),
  });

  const atualizar = useMutation({
    mutationFn: atualizarCobrancas,
    onSuccess: () => toast.success("Cobranças atualizadas."),
  });

  const actions: RowAction<Instituicao>[] = [
    { label: "Editar", icon: Pencil, onClick: (row) => navigate(`/Instituicao/Form/${row.id}`) },
    { label: "Configurações", icon: Settings, onClick: (row) => navigate(`/Instituicao/Configuracao/${row.id}`) },
    { label: "Acessar ficha", icon: Eye, onClick: (row) => navigate(`/Instituicao/Ficha/${row.id}`) },
    {
      label: "Gerar backup dos arquivos assinados",
      icon: Download,
      onClick: (row) => backup.mutate(row.id),
    },
    { label: "Acessar cobranças", icon: CircleDollarSign, href: (row) => `/Cobranca/Consulta/Instituicao/${row.id}` },
    {
      label: "Atualizar cobranças",
      icon: RefreshCw,
      onClick: (row) => atualizar.mutate(row.id),
      confirm: { title: "Atualizar cobranças?", description: () => "Deseja atualizar as cobranças desta instituição?", confirmLabel: "Atualizar" },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-foreground">Instituições</h1>
        <div className="flex items-center gap-3">
          {backup.isPending && <span className="text-sm text-muted-foreground">Gerando arquivo...</span>}
          <Button asChild>
            <Link to="/Instituicao/Form">
              <Plus className="mr-2 h-4 w-4" /> Adicionar Novo
            </Link>
          </Button>
        </div>
      </div>

      <DataTable
        query={query}
        columns={columns}
        actions={actions}
        getRowId={(r) => r.id}
        exportFilename="instituicoes"
        emptyMessage="Nenhuma instituição encontrada."
        toolbar={
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-28">
              <Label htmlFor="f-codigo">Código</Label>
              <Input
                id="f-codigo"
                value={query.draft.codigo ?? ""}
                onChange={(e) => query.setDraft((f) => ({ ...f, codigo: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && query.search()}
              />
            </div>
            <div className="w-64">
              <Label htmlFor="f-razao">Razão social / Nome fantasia</Label>
              <Input
                id="f-razao"
                value={query.draft.razaoSocial ?? ""}
                onChange={(e) => query.setDraft((f) => ({ ...f, razaoSocial: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && query.search()}
              />
            </div>
            <div className="w-48">
              <Label htmlFor="f-cnpj">CNPJ</Label>
              <Input
                id="f-cnpj"
                value={query.draft.cnpj ?? ""}
                onChange={(e) => query.setDraft((f) => ({ ...f, cnpj: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && query.search()}
              />
            </div>
            <div className="w-40">
              <Label htmlFor="f-situacao">Situação</Label>
              <Select
                value={query.draft.situacaoAdmin ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, situacaoAdmin: v === "__all__" ? undefined : v }))}
              >
                <SelectTrigger id="f-situacao">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SITUACAO_ADMIN_LABEL).map(([v, label]) => (
                    <SelectItem key={v} value={v}>
                      {label}
                    </SelectItem>
                  ))}
                  <SelectItem value="__all__">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <Label htmlFor="f-estado">Estado</Label>
              <Select
                value={query.draft.estado ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, estado: v === "__all__" ? undefined : v }))}
              >
                <SelectTrigger id="f-estado">
                  <SelectValue placeholder="Selecione o Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Selecione o Estado</SelectItem>
                  {ESTADOS_BR.map((uf) => (
                    <SelectItem key={uf.value} value={uf.value}>
                      {uf.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label htmlFor="f-situacao-cs">Situação CS</Label>
              <Select
                value={query.draft.situacaoCs ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, situacaoCs: v === "__all__" ? undefined : v }))}
              >
                <SelectTrigger id="f-situacao-cs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SITUACAO_CS_LABEL).map(([v, label]) => (
                    <SelectItem key={v} value={v}>
                      {label}
                    </SelectItem>
                  ))}
                  <SelectItem value="__all__">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label htmlFor="f-financeiro">Financeiro</Label>
              <Select
                value={query.draft.financeiro ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, financeiro: v === "__all__" ? undefined : v }))}
              >
                <SelectTrigger id="f-financeiro">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IN">Inadimplentes</SelectItem>
                  <SelectItem value="BL">Bloqueados</SelectItem>
                  <SelectItem value="__all__">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label htmlFor="f-svp">Associado São Vicente de Paulo</Label>
              <Select
                value={query.draft.seSaoVicenteDePaulo ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, seSaoVicenteDePaulo: v === "__all__" ? undefined : v }))}
              >
                <SelectTrigger id="f-svp">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  <SelectItem value="S">Sim</SelectItem>
                  <SelectItem value="N">Não</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={query.search}>Buscar</Button>
          </div>
        }
      />
    </div>
  );
}
